import { FileDoc, FilePdf, FilePpt, File as FileIcon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { isImageMime } from './attachments';

export interface RenderedAttachment {
  url: string;
  mime: string;
}

/* IMAGE_MIMES se rend en vignette, DOC_MIMES en pastille nommee. Un PDF servi
   dans une balise image ne donne qu'un cadre casse : la distinction vient de
   attachments.ts et vaut pour les trois ecrans de chat. */
function docChip(mime: string): { Icon: typeof FilePdf; label: string } {
  if (mime === 'application/pdf') return { Icon: FilePdf, label: 'PDF' };
  if (mime.includes('wordprocessingml')) return { Icon: FileDoc, label: 'Word' };
  if (mime.includes('presentationml')) return { Icon: FilePpt, label: 'Présentation' };
  return { Icon: FileIcon, label: 'Document' };
}

const THUMB = {
  lg: 'max-w-[220px] max-h-[220px] rounded-[12px] border border-border object-cover',
  sm: 'w-16 h-16 rounded-[10px] object-cover border border-primary-foreground/30',
};

const CHIP = {
  card: 'border border-border bg-card shadow-card text-foreground',
  onPrimary: 'bg-primary-foreground/15 text-primary-foreground',
};

/**
 * Pieces jointes d'un message, relues depuis l'historique. Les URL viennent
 * signees par l'API a chaque chargement — rien ici ne les met en cache.
 */
export function AttachmentList({
  attachments,
  size = 'lg',
  tone = 'card',
  className,
}: {
  attachments: readonly RenderedAttachment[] | null | undefined;
  size?: keyof typeof THUMB;
  tone?: keyof typeof CHIP;
  className?: string;
}) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={cn('flex gap-2 flex-wrap', className)}>
      {attachments.map((a, i) => {
        if (isImageMime(a.mime)) {
          return (
            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block">
              <img src={a.url} alt="" className={THUMB[size]} />
            </a>
          );
        }
        const { Icon, label } = docChip(a.mime);
        return (
          <a
            key={i}
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 min-h-9 pointer-coarse:min-h-11 rounded-[10px] text-[12.5px] font-bold no-underline',
              CHIP[tone],
            )}
          >
            <Icon size={17} weight="bold" />
            {label}
          </a>
        );
      })}
    </div>
  );
}
